import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import ActiveSubscriptionSummary from '../../component/ActiveSubscriptionCard';
import './SuggestedCustomers.css';

import { formatDateOnly } from '../../../utils/dateUtils';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({ baseURL: API_BASE_URL });

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return formatDateOnly(dateStr);
};

function SuggestedCustomers({ embedded = false }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
    const [searchName, setSearchName] = useState('');
    const [searchPhone, setSearchPhone] = useState('');
    const [searchCategory, setSearchCategory] = useState('');
    const [searchSource, setSearchSource] = useState('');
    const [unlockingId, setUnlockingId] = useState(null);

    const fetchData = React.useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get('/recommendations/vendor/suggested-customers', {
                params: { 
                    page, 
                    limit: 10,
                    name: searchName,
                    phone: searchPhone,
                    category: searchCategory,
                    source: searchSource
                }
            });
            if (response.data.success) {
                setData(response.data.data);
                setPagination({
                    page: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    totalItems: response.data.totalItems
                });
            }
        } catch (err) {
            setError('Failed to fetch suggested customers.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [searchName, searchPhone, searchCategory, searchSource]);

    // Re-fetch when filters change (resetting to page 1)
    useEffect(() => { 
        fetchData(1); 
    }, [fetchData]);

    const unlockContact = async (row) => {
        const result = await Swal.fire({
            title: 'Unlock contact details?',
            text: 'This will use 1 lead from your available lead balance.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#08a88a',
            confirmButtonText: 'Yes, unlock'
        });
        if (!result.isConfirmed) return;

        try {
            setUnlockingId(row.id);
            let endpoint = `/recommendations/vendor/suggested-customers/${row.id}/unlock`;
            if (row.type === 'vendor_lead') {
                endpoint = `/vendors/leads/${row.id}/unlock`;
            }
            const response = await apiClient.post(endpoint);
            if (response.data.success) {
                window.dispatchEvent(new CustomEvent('lead-balance-updated', {
                    detail: { availableLeads: response.data.available_leads }
                }));
                window.dispatchEvent(new CustomEvent('suggested-customer-unlocked'));
                await fetchData(pagination.page);
                Swal.fire('Lead added', 'The customer has been moved to your Leads List.', 'success');
            }
        } catch (err) {
            Swal.fire('Unable to unlock', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setUnlockingId(null);
        }
    };

    const contactValue = (row, type) => {
        const value = type === 'phone' ? row.session?.customer_mobile : row.session?.customer_email;
        if (row.is_unlocked) {
            return type === 'phone' ? (
                <a href={`tel:${value}`} className="text-decoration-none text-primary font-medium">
                    <i className="bi bi-telephone me-1"></i>{value || '—'}
                </a>
            ) : (value || '—');
        }
        return (
            <div 
                className="sc-locked-contact"
                onClick={() => unlockContact(row)}
                title="Click to unlock contact details (uses 1 lead)"
            >
                <span className="sc-locked-text">{value || '—'}</span>
                <button 
                    type="button" 
                    className="sc-unlock-eye"
                    aria-label="Unlock phone and email"
                    disabled={unlockingId === row.id} 
                    onClick={(e) => {
                        e.stopPropagation();
                        unlockContact(row);
                    }}
                >
                    <i className={unlockingId === row.id ? 'spinner-border spinner-border-sm text-white' : 'ri-eye-fill'}></i>
                </button>
            </div>
        );
    };

    const filteredData = data;

    if (embedded && !loading && !error && data.length === 0) return null;

    return (
        <div className={embedded ? '' : 'container-fluid'}>
            {!embedded && (
                <>
                    <div className="row">
                        <div className="col-12">
                            <ActiveSubscriptionSummary />
                        </div>
                    </div>
                </>
            )}

            <div className={embedded ? '' : 'row'}>
                <div className={embedded ? '' : 'col-12'}>
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">
                                <i className="bi bi-people-fill me-2 text-success"></i>
                                Discover Customers ⭐
                            </h5>
                        </div>
                        <div className="card-body">
                            {/* Filters Row */}
                            <div className="row g-2 mb-4 align-items-center bg-light p-3 rounded">
                                <div className="col-12 col-sm-6 col-md-3">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search by name..."
                                        value={searchName}
                                        onChange={e => setSearchName(e.target.value)}
                                    />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search by phone..."
                                        value={searchPhone}
                                        onChange={e => setSearchPhone(e.target.value)}
                                    />
                                </div>
                                <div className="col-12 col-sm-6 col-md-2">
                                    <select 
                                        className="form-select form-select-sm" 
                                        value={searchCategory}
                                        onChange={e => setSearchCategory(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        <option value="holidays">Holidays</option>
                                        <option value="hotels">Hotels</option>
                                        <option value="cab">Cab</option>
                                        <option value="tirth_yatra">Tirth Yatra</option>
                                        <option value="medical_tourism">Medical Tourism</option>
                                    </select>
                                </div>
                                <div className="col-12 col-sm-6 col-md-2">
                                    <select 
                                        className="form-select form-select-sm" 
                                        value={searchSource}
                                        onChange={e => setSearchSource(e.target.value)}
                                    >
                                        <option value="">All Sources</option>
                                        <option value="Recommended">Recommended</option>
                                        <option value="Suggested">Suggested</option>
                                        <option value="Onboarding">Onboarding</option>
                                    </select>
                                </div>
                                <div className="col-12 col-sm-12 col-md-2 text-md-end mt-2 mt-md-0">
                                    <button 
                                        className="btn btn-sm btn-secondary w-100"
                                        onClick={() => {
                                            setSearchName('');
                                            setSearchPhone('');
                                            setSearchCategory('');
                                            setSearchSource('');
                                        }}
                                    >
                                        Reset Filters
                                    </button>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="text-muted mt-3">Loading customer data...</p>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger" role="alert">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                </div>
                            ) : filteredData.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-inbox fs-1 text-muted"></i>
                                    <p className="text-muted mt-3">
                                        {(searchName || searchPhone || searchCategory || searchSource) ? 'No results found.' : 'No customers have received your recommendation yet.'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0" id="suggested-customers-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Customer Name</th>
                                                    <th>Phone</th>
                                                    <th>Email</th>
                                                    <th>Category</th>
                                                    <th>Source</th>
                                                    <th>Date Sent</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredData.map((row, idx) => (
                                                    <tr key={row.id}>
                                                        <td>{(pagination.page - 1) * 10 + idx + 1}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="sc-avatar">
                                                                    {(row.session?.customer_name || 'C').charAt(0).toUpperCase()}
                                                                </div>
                                                                <span className="fw-semibold">{row.session?.customer_name || '—'}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {contactValue(row, 'phone')}
                                                        </td>
                                                        <td>{contactValue(row, 'email')}</td>
                                                        <td className="category-badge">
                                                            <span className="badge bg-light text-dark border category-badge">
                                                                {row.session?.category
                                                                    ? row.session.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                                                    : '—'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${row.source === 'Recommended' ? 'bg-primary' : row.source === 'Suggested' ? 'bg-info' : row.source === 'Onboarding' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                                {row.source || '—'}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(row.sent_at || row.created_at)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {pagination.totalPages > 1 && (
                                        <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                                            <p className="text-muted mb-0 small">
                                                Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalItems} total)
                                            </p>
                                            <nav>
                                                <ul className="pagination pagination-sm mb-0">
                                                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => fetchData(pagination.page - 1)}>
                                                            <i className="bi bi-chevron-left"></i>
                                                        </button>
                                                    </li>
                                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                                        <li key={p} className={`page-item ${p === pagination.page ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => fetchData(p)}>{p}</button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => fetchData(pagination.page + 1)}>
                                                            <i className="bi bi-chevron-right"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SuggestedCustomers;
